import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * InfoTooltip — portal-based (i) icon with a floating tooltip. Ported from
 * the dane etf-website `components/InfoTooltip.tsx`.
 */
export function InfoTooltip({
  tip,
  direction = "center",
}: {
  tip: string;
  direction?: "center" | "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x =
      direction === "left" ? r.right : direction === "right" ? r.left : r.left + r.width / 2;
    setCoords({ x, y: r.top });
    setOpen(true);
  }, [direction]);

  const hide = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, hide]);

  const tooltipTransform =
    direction === "left"
      ? "translate(calc(-100% + 6px), calc(-100% - 8px))"
      : direction === "right"
        ? "translate(-6px, calc(-100% - 8px))"
        : "translate(-50%, calc(-100% - 8px))";

  return (
    <>
      <span
        ref={ref}
        aria-label={tip}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex items-center justify-center align-middle w-[13px] h-[13px] rounded-full border border-[var(--etf-white3)] text-[var(--etf-white3)] text-[9px] font-semibold font-[family-name:var(--font-instrument)] ml-[5px] cursor-help outline-none focus:ring-1 focus:ring-[var(--etf-orange)] not-italic"
      >
        i
      </span>
      {mounted &&
        open &&
        coords &&
        createPortal(
          <div
            role="tooltip"
            style={{
              left: coords.x,
              top: coords.y,
              transform: tooltipTransform,
              backgroundColor: "#1e1e1e",
              borderColor: "rgba(255,255,255,0.13)",
              color: "#f5f2ee",
            }}
            className="fixed z-[9999] w-[260px] max-[768px]:w-[180px] max-w-[80vw] px-[14px] py-[12px] rounded-md border text-[12px] font-normal font-[family-name:var(--font-instrument)] leading-[1.55] tracking-[0.01em] text-left whitespace-normal shadow-[0_8px_24px_rgba(0,0,0,0.6)] pointer-events-none"
          >
            {tip}
          </div>,
          document.body,
        )}
    </>
  );
}
